import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ScanLine, AlertCircle, CheckCircle2, ExternalLink, Loader2, Globe2 } from "lucide-react";

export default function QrScans() {
  const [scans, setScans] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterExt, setFilterExt] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [a, b] = await Promise.all([
      api.get("/qr-scans", { params: { only_external: filterExt, limit: 200 } }),
      api.get("/qr-scans/stats"),
    ]);
    setScans(a.data); setStats(b.data); setLoading(false);
  }, [filterExt]);
  useEffect(() => { load(); }, [load]);

  const fmt = (iso) => iso?.slice(0, 19).replace("T", " ");

  return (
    <div data-testid="qr-scans-page">
      <PageHeader
        title="Vérifications QR externes"
        subtitle="Chaque scan d'un QR code de vérification (DGFiP, URSSAF, banque, partenaire) est tracé ici en temps réel."
      />

      {!loading && stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Stat label="Total scans" value={stats.total_scans} icon={ScanLine} color="#C5A059" />
          <Stat label="Scans externes" value={stats.external_scans} icon={Globe2} color="#3B82F6" testId="stat-ext" />
          <Stat label="Documents invalides" value={stats.invalid_scans} icon={AlertCircle} color="#EF4444" />
          <Stat label="Documents authentiques" value={stats.total_scans - stats.invalid_scans} icon={CheckCircle2} color="#10B981" />
        </div>
      )}

      <Tabs defaultValue="journal">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="journal" data-testid="tab-journal-scans">Journal des scans</TabsTrigger>
          <TabsTrigger value="top" data-testid="tab-top">Documents les plus consultés</TabsTrigger>
        </TabsList>

        <TabsContent value="journal" className="mt-5">
          <div className="flex items-center gap-2 mb-3">
            <Button
              variant={filterExt ? "default" : "outline"}
              size="sm"
              onClick={() => setFilterExt(!filterExt)}
              data-testid="filter-external"
              className={filterExt ? "bg-[#C5A059] hover:bg-[#A88846] text-white" : ""}
            >
              <Globe2 className="h-3 w-3 mr-1" />
              {filterExt ? "Filtrer : externes uniquement" : "Afficher tous les scans"}
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div>
          ) : scans.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center">
              <ScanLine className="h-10 w-10 mx-auto text-slate-300" />
              <p className="mt-3 text-slate-500">Aucun scan enregistré pour l&apos;instant.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3">Horodatage</th>
                    <th className="px-4 py-3">Document</th>
                    <th className="px-4 py-3">Entreprise</th>
                    <th className="px-4 py-3">Origine</th>
                    <th className="px-4 py-3">Statut</th>
                  </tr>
                </thead>
                <tbody>{scans.map(s => (
                  <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 text-xs text-slate-600 font-mono">{fmt(s.scanned_at)}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700 mr-2 uppercase">{s.doc_type}</span>
                      <span className="font-mono text-xs">{s.doc_reference || s.doc_id?.slice(0,8)}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-700 text-xs">{s.entreprise_nom || "—"}</td>
                    <td className="px-4 py-3 text-xs">
                      <div className="flex items-center gap-2">
                        {s.is_external && <ExternalLink className="h-3 w-3 text-[#C5A059]" />}
                        <code className="bg-slate-50 px-1.5 py-0.5 rounded text-slate-700">{s.ip}</code>
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5 truncate max-w-xs">{s.user_agent}</div>
                    </td>
                    <td className="px-4 py-3">
                      {s.valid
                        ? <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-700"><CheckCircle2 className="h-3 w-3" />Authentique</span>
                        : <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-red-100 text-red-700"><AlertCircle className="h-3 w-3" />Invalide</span>}
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="top" className="mt-5">
          {!stats || stats.top_documents.length === 0 ? (
            <div className="text-slate-500">Pas encore assez de données.</div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden max-w-2xl">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200"><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="px-4 py-3">Type</th><th className="px-4 py-3">Référence</th><th className="px-4 py-3 text-right">Nb scans</th></tr></thead>
                <tbody>{stats.top_documents.map((d, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700 uppercase">{d.type}</span></td>
                    <td className="px-4 py-3 font-mono text-xs">{d.reference}</td>
                    <td className="px-4 py-3 text-right font-semibold">{d.scans}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value, icon: Icon, color, testId }) {
  return (
    <div className="kpi-card bg-white border border-slate-200 rounded-xl p-5" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: `${color}1A`, color }}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">{label}</div>
      </div>
      <div className="mt-3 text-2xl font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
    </div>
  );
}
